import {
  AAD_FILEMETA_PREFIX,
  AAD_MANIFEST_PREFIX,
  FILE_ID_RE,
  MAX_FILE_NAME_LENGTH,
  MAX_MANIFEST_CIPHERTEXT,
  MAX_MESSAGE_LENGTH,
  MAX_MIME_LENGTH,
  MAX_PATH_LENGTH,
  MAX_SENDER_LENGTH,
  NONCE_SIZE,
  TRANSFER_ID_RE,
} from "./constants.js";
import { concat, fromBase64Url, randomBytes, toBase64Url, utf8, utf8Decode } from "./bytes.js";
import { LscError } from "./errors.js";
import { toAesKey } from "./keys.js";

export interface ManifestFile {
  id: string;
  name: string;
  /** plaintext length */
  size: number;
  /** MIME type or "" */
  type: string;
  mtime?: number;
  /** relative folder path, "" or "a/b/" */
  path?: string;
}

export interface ManifestRecipient {
  id: string;
  label: string;
}

export interface Manifest {
  v: 1;
  files: ManifestFile[];
  message?: string;
  sender?: string;
  created: number;
  recipients?: ManifestRecipient[];
}

export interface FileMeta {
  name: string;
  size: number;
  type: string;
  mtime?: number;
}

/* ------------------------------------------------------------------ */
/* Generic sealed-JSON helper: base64url( nonce(12) || AES-GCM(K, nonce, json, AAD) ) */

async function sealJson(
  key: CryptoKey | Uint8Array,
  aad: Uint8Array,
  value: unknown,
  nonce: Uint8Array = randomBytes(NONCE_SIZE),
): Promise<string> {
  if (nonce.length !== NONCE_SIZE) throw new LscError("BAD_INPUT", "nonce must be 12 bytes");
  const k = await toAesKey(key, ["encrypt"]);
  const pt = utf8(JSON.stringify(value));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce, additionalData: aad, tagLength: 128 }, k, pt),
  );
  const sealed = concat(nonce, ct);
  if (sealed.length > MAX_MANIFEST_CIPHERTEXT) {
    throw new LscError("BAD_INPUT", `sealed payload exceeds ${MAX_MANIFEST_CIPHERTEXT} bytes`);
  }
  return toBase64Url(sealed);
}

async function openJson(key: CryptoKey | Uint8Array, aad: Uint8Array, sealedB64: string): Promise<unknown> {
  const sealed = fromBase64Url(sealedB64);
  if (sealed.length < NONCE_SIZE + 16 || sealed.length > MAX_MANIFEST_CIPHERTEXT) {
    throw new LscError("BAD_INPUT", "sealed payload has invalid length");
  }
  const k = await toAesKey(key, ["decrypt"]);
  let pt: ArrayBuffer;
  try {
    pt = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: sealed.subarray(0, NONCE_SIZE), additionalData: aad, tagLength: 128 },
      k,
      sealed.subarray(NONCE_SIZE),
    );
  } catch {
    throw new LscError("INTEGRITY_FAILED", "sealed payload");
  }
  try {
    return JSON.parse(utf8Decode(new Uint8Array(pt)));
  } catch {
    throw new LscError("BAD_INPUT", "sealed payload is not valid JSON");
  }
}

/* ------------------------------------------------------------------ */
/* Manifest (§6) */

function manifestAad(transferId: string): Uint8Array {
  assertTransferId(transferId);
  return utf8(AAD_MANIFEST_PREFIX + transferId);
}

export async function encryptManifest(
  manifestKey: CryptoKey | Uint8Array,
  transferId: string,
  manifest: Manifest,
): Promise<string> {
  validateManifest(manifest);
  return sealJson(manifestKey, manifestAad(transferId), manifest);
}

export async function decryptManifest(
  manifestKey: CryptoKey | Uint8Array,
  transferId: string,
  encryptedManifest: string,
): Promise<Manifest> {
  const value = await openJson(manifestKey, manifestAad(transferId), encryptedManifest);
  validateManifest(value);
  return value;
}

/** Test-only: deterministic nonce for vector generation. Never use with a fixed nonce in production. */
export async function _encryptManifestWithNonce(
  manifestKey: CryptoKey | Uint8Array,
  transferId: string,
  manifest: Manifest,
  nonce: Uint8Array,
): Promise<string> {
  validateManifest(manifest);
  return sealJson(manifestKey, manifestAad(transferId), manifest, nonce);
}

/* ------------------------------------------------------------------ */
/* Per-file meta (§7.2) */

function fileMetaAad(transferId: string, fileId: string): Uint8Array {
  assertTransferId(transferId);
  if (!FILE_ID_RE.test(fileId)) throw new LscError("BAD_INPUT", "invalid fileId");
  return utf8(`${AAD_FILEMETA_PREFIX}${transferId}/${fileId}`);
}

export async function encryptFileMeta(
  fileKey: CryptoKey | Uint8Array,
  transferId: string,
  fileId: string,
  meta: FileMeta,
): Promise<string> {
  validateFileMeta(meta);
  return sealJson(fileKey, fileMetaAad(transferId, fileId), meta);
}

export async function decryptFileMeta(
  fileKey: CryptoKey | Uint8Array,
  transferId: string,
  fileId: string,
  encryptedMeta: string,
): Promise<FileMeta> {
  const value = await openJson(fileKey, fileMetaAad(transferId, fileId), encryptedMeta);
  validateFileMeta(value);
  return value;
}

/* ------------------------------------------------------------------ */
/* Validation */

function assertTransferId(transferId: string): void {
  if (!TRANSFER_ID_RE.test(transferId)) throw new LscError("BAD_INPUT", "invalid transferId");
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isSafeNonNegInt(v: unknown): v is number {
  return typeof v === "number" && Number.isSafeInteger(v) && v >= 0;
}

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

function validateName(name: unknown, field: string): asserts name is string {
  if (typeof name !== "string" || name.length === 0 || name.length > MAX_FILE_NAME_LENGTH) {
    throw new LscError("BAD_INPUT", `${field}: invalid length`);
  }
  if (name.includes("/") || name.includes("\\") || CONTROL_CHARS.test(name) || name === "." || name === "..") {
    throw new LscError("BAD_INPUT", `${field}: forbidden characters`);
  }
}

function validatePath(path: unknown): void {
  if (path === undefined || path === "") return;
  if (typeof path !== "string" || path.length > MAX_PATH_LENGTH || CONTROL_CHARS.test(path) || path.includes("\\")) {
    throw new LscError("BAD_INPUT", "path: invalid");
  }
  if (path.startsWith("/") || !path.endsWith("/")) throw new LscError("BAD_INPUT", "path must be relative and end with /");
  for (const seg of path.slice(0, -1).split("/")) {
    if (seg === "" || seg === "." || seg === "..") throw new LscError("BAD_INPUT", "path: invalid segment");
  }
}

function validateType(type: unknown): asserts type is string {
  if (typeof type !== "string" || type.length > MAX_MIME_LENGTH || CONTROL_CHARS.test(type)) {
    throw new LscError("BAD_INPUT", "type: invalid");
  }
}

export function validateFileMeta(v: unknown): asserts v is FileMeta {
  if (!isRecord(v)) throw new LscError("BAD_INPUT", "fileMeta must be an object");
  validateName(v["name"], "name");
  if (!isSafeNonNegInt(v["size"])) throw new LscError("BAD_INPUT", "size: invalid");
  validateType(v["type"]);
  if (v["mtime"] !== undefined && !isSafeNonNegInt(v["mtime"])) throw new LscError("BAD_INPUT", "mtime: invalid");
}

export function validateManifest(v: unknown): asserts v is Manifest {
  if (!isRecord(v)) throw new LscError("BAD_INPUT", "manifest must be an object");
  if (v["v"] !== 1) throw new LscError("BAD_INPUT", "manifest.v must be 1");
  if (!isSafeNonNegInt(v["created"])) throw new LscError("BAD_INPUT", "manifest.created: invalid");
  const files = v["files"];
  if (!Array.isArray(files) || files.length === 0 || files.length > 1000) {
    throw new LscError("BAD_INPUT", "manifest.files: must contain 1..1000 entries");
  }
  const ids = new Set<string>();
  for (const f of files) {
    if (!isRecord(f)) throw new LscError("BAD_INPUT", "file entry must be an object");
    if (typeof f["id"] !== "string" || !FILE_ID_RE.test(f["id"])) throw new LscError("BAD_INPUT", "file.id: invalid");
    if (ids.has(f["id"])) throw new LscError("BAD_INPUT", "file.id: duplicate");
    ids.add(f["id"]);
    validateName(f["name"], "file.name");
    if (!isSafeNonNegInt(f["size"])) throw new LscError("BAD_INPUT", "file.size: invalid");
    validateType(f["type"]);
    if (f["mtime"] !== undefined && !isSafeNonNegInt(f["mtime"])) throw new LscError("BAD_INPUT", "file.mtime: invalid");
    validatePath(f["path"]);
  }
  if (v["message"] !== undefined) {
    if (typeof v["message"] !== "string" || v["message"].length > MAX_MESSAGE_LENGTH) {
      throw new LscError("BAD_INPUT", "manifest.message: invalid");
    }
  }
  if (v["sender"] !== undefined) {
    if (typeof v["sender"] !== "string" || v["sender"].length > MAX_SENDER_LENGTH || CONTROL_CHARS.test(v["sender"])) {
      throw new LscError("BAD_INPUT", "manifest.sender: invalid");
    }
  }
  if (v["recipients"] !== undefined) {
    const r = v["recipients"];
    if (!Array.isArray(r) || r.length > 100) throw new LscError("BAD_INPUT", "manifest.recipients: invalid");
    for (const x of r) {
      if (!isRecord(x) || typeof x["id"] !== "string" || typeof x["label"] !== "string" || x["label"].length > 128) {
        throw new LscError("BAD_INPUT", "recipient: invalid");
      }
    }
  }
}
